/**
 * BarometerHero — kompakter Above-the-Fold-Hero für alle Barometer-Seiten.
 * Enthält: H1, Subtext, Social Proof, URL-Eingabe, ein CTA, Trust-Strip.
 * Alles above the fold ohne Scrollen (Desktop + Mobile).
 */
import { useState, KeyboardEvent } from "react";

const PORTRAIT_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310519663476281654/fDRgangGWFphFKzWtNAEav/larissa-portrait_abbef783.jpg";
const MEETERGO_URL = "https://cal.meetergo.com/larissa-girenko/30-min-meeting-or-larissa-girenko?utm_source=ig&utm_medium=social&utm_content=link_in_bio";

interface BarometerHeroProps {
  /** Barometer-Name z.B. "SEO-Barometer™" */
  name: string;
  /** Kurze Schmerz-Frage als H1 */
  headline: string;
  /** Subtext unter der H1 */
  subtext: string;
  /** Akzentfarbe des Barometers */
  accentColor: string;
  /** CTA-Button-Farbe (Standard: #115751) */
  ctaColor?: string;
  /** Stats-Kacheln rechts neben dem Portrait */
  stats: Array<{ val: string; label: string }>;
  /** Aktueller URL-Wert */
  url: string;
  /** URL-Setter */
  onUrlChange: (url: string) => void;
  /** Wird ausgelöst wenn der CTA geklickt wird */
  onSubmit: () => void;
  /** Ist der Submit-Button deaktiviert (z.B. während Analyse läuft) */
  isLoading?: boolean;
  /** Fehlertext unter der Eingabe */
  error?: string | null;
  /** Admin-Modus: zeigt anderen Button-Text und Hinweis */
  isAdmin?: boolean;
  /** Admin-CTA-Text */
  adminCtaText?: string;
}

export function BarometerHero({
  name,
  headline,
  subtext,
  accentColor,
  ctaColor = "#115751",
  stats,
  url,
  onUrlChange,
  onSubmit,
  isLoading = false,
  error,
  isAdmin = false,
  adminCtaText = "Vollanalyse starten →",
}: BarometerHeroProps) {
  const handleKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") onSubmit();
  };

  return (
    <>
      {/* ── HERO ── */}
      <section className="w-full bg-black">
        <div className="max-w-5xl mx-auto px-6 py-10 md:py-12">
          <div className="flex flex-col md:flex-row md:items-start md:gap-10">

            {/* Linke Spalte */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold tracking-widest uppercase mb-3"
                style={{ color: accentColor, fontFamily: "'DM Sans', sans-serif" }}>
                {name} · WERBEAGENTUR Larissa Girenko
              </p>
              <h1 className="font-black uppercase leading-none mb-3 text-white"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.4rem, 5.5vw, 4rem)" }}
                dangerouslySetInnerHTML={{ __html: headline }} />
              <p className="text-gray-300 text-sm leading-relaxed mb-4 max-w-lg"
                style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {subtext}
              </p>

              {/* Social Proof */}
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <a href="https://www.google.com/maps/search/WERBEAGENTUR+Larissa+Girenko"
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-1.5 border border-white/20 hover:border-white/40 transition-colors">
                  <span className="text-yellow-400 text-xs">★★★★★</span>
                  <span className="text-white text-xs font-bold" style={{ fontFamily: "'DM Sans', sans-serif" }}>5,0 · Google</span>
                </a>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>17+ Jahre Erfahrung</span>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>Harvard & NNG-Methodik</span>
              </div>

              {/* URL-Eingabe */}
              <div className="mb-3">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => onUrlChange(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="https://deine-website.de"
                  className="w-full bg-white/10 border border-white/30 text-white placeholder-gray-500 px-4 py-3 text-sm focus:outline-none focus:border-white/60"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                />
              </div>

              {error && (
                <div className="border-l-4 border-red-500 bg-red-900/30 px-4 py-3 mb-3">
                  <p className="text-sm text-red-300" style={{ fontFamily: "'DM Sans', sans-serif" }}>{error}</p>
                </div>
              )}

              {isAdmin ? (
                <div className="space-y-2">
                  <div className="border border-dashed border-white/20 px-4 py-2 text-center">
                    <p className="text-xs text-gray-400 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>🔑 Admin-Modus: Vollanalyse</p>
                  </div>
                  <button
                    onClick={onSubmit}
                    disabled={!url.trim() || isLoading}
                    className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-4 transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: accentColor, fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {isLoading ? "Analysiert …" : adminCtaText}
                  </button>
                </div>
              ) : (
                <button
                  onClick={onSubmit}
                  disabled={!url.trim() || isLoading}
                  className="w-full text-white font-bold text-sm uppercase tracking-widest px-6 py-4 transition-opacity hover:opacity-90 disabled:opacity-40"
                  style={{ backgroundColor: ctaColor, fontFamily: "'DM Sans', sans-serif" }}
                >
                  {isLoading ? "Analysiert …" : "URL eingeben & Anfrage senden →"}
                </button>
              )}

              <div className="flex flex-wrap gap-3 mt-3">
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Persönlich von Larissa analysiert</span>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Kein Account nötig</span>
                <span className="text-gray-500 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>✓ Lieferung in 72h</span>
              </div>
            </div>

            {/* Rechte Spalte: Portrait + Stats (Desktop only) */}
            <div className="hidden md:flex flex-col items-center gap-4 flex-shrink-0" style={{ width: "180px" }}>
              <div className="relative overflow-hidden" style={{ width: "160px", height: "160px" }}>
                <div className="absolute inset-0 z-10"
                  style={{ background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.7) 100%)" }} />
                <div className="absolute top-0 left-0 right-0 h-0.5 z-20" style={{ backgroundColor: accentColor }} />
                <img src={PORTRAIT_IMG} alt="Larissa Girenko – Marketing-Expertin"
                  className="w-full h-full object-cover object-top" />
              </div>
              <div className="w-full space-y-2">
                {stats.map((s) => (
                  <div key={s.label} className="border-l-2 pl-3" style={{ borderColor: accentColor }}>
                    <p className="text-white font-bold text-lg leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{s.val}</p>
                    <p className="text-gray-400 text-xs" style={{ fontFamily: "'DM Sans', sans-serif" }}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Trust-Strip ── */}
      <div className="border-b border-gray-200 py-4 px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-yellow-500 text-sm">★★★★★</span>
            <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>5,0 · 5 Google-Bewertungen</span>
          </div>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>17+ Jahre Erfahrung</span>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>Persönliche Analyse von Larissa</span>
          <div className="w-px h-4 bg-gray-300 hidden sm:block" />
          <span className="text-xs text-gray-600 font-medium" style={{ fontFamily: "'DM Sans', sans-serif" }}>Analyse ab 97 €</span>
        </div>
      </div>
    </>
  );
}
